/**
 * SlideCard
 *
 * A compact thumbnail card for a single slide shown in the library grid.
 * Displays:
 *  - Lazy-loaded thumbnail image (fetched via API)
 *  - AI title, category badge, and source filename
 *
 * The card is a drag source via `useDraggable` from @dnd-kit/core so users
 * can drag slides into the builder.  Clicking the card triggers the
 * `onPreview` callback to open the preview modal.
 *
 * When `slide.processing_state === "render_failed"` the card is rendered
 * in a non-interactive disabled state with a "render failed" badge.
 */

import { type PointerEvent as ReactPointerEvent, useCallback } from "react"

import { useDraggable } from "@dnd-kit/core"
import { formatDistanceToNow } from "date-fns"
import { useTranslation } from "react-i18next"

import type { SlideSenseSlide } from "@/features/slide-sense/types"

import { AlertTriangle, EyeOff, GripVertical, Plus, Presentation } from "@/components/icons"
import { cn } from "@/lib/utils"

import { SlideCategoryBadge } from "./SlideCategoryBadge"
import { SlideThumbnailImage } from "./SlideThumbnailImage"
import { useSlideThumbnailLazyRequest } from "../hooks/use-thumbnail-lazy-request"
import { getDateFnsLocale } from "../utils/date-format"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SlideCardProps {
  slide: SlideSenseSlide
  onPreview?: (slide: SlideSenseSlide) => void
  /** Callback to add this slide directly to the builder (e.g. via "+" button). */
  onAdd?: (slide: SlideSenseSlide) => void
  /** AI search match reason (shown when in AI search mode). */
  aiSearchReason?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SlideCard({ slide, onPreview, onAdd, aiSearchReason }: SlideCardProps) {
  const { t, i18n } = useTranslation()
  const renderFailed = slide.processing_state === "render_failed"
  const updatedAt = slide.updated_at ?? slide.created_at
  const relativeUpdatedAt = (() => {
    if (!updatedAt) return null
    const date = new Date(updatedAt)
    if (Number.isNaN(date.getTime())) return null
    const locale = getDateFnsLocale(i18n.language)
    return formatDistanceToNow(date, { addSuffix: true, locale })
  })()
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: `slide-${slide.id}`,
    data: { type: "slide-sense-slide", slide },
    disabled: renderFailed,
  })

  // Viewport-aware thumbnail fetch: this ref triggers a queued batch
  // request when the card scrolls near the viewport. Merged with
  // dnd-kit's setNodeRef so both pieces of behaviour share the same DOM
  // node.
  const lazyRef = useSlideThumbnailLazyRequest(slide.id)
  const mergedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node)
      lazyRef.current = node
    },
    [setNodeRef, lazyRef]
  )

  // Pointer drag starts from anywhere on the card (root), while keyboard drag
  // is activated only from the dedicated drag handle. dnd-kit's KeyboardSensor
  // ignores key events whose target is not the activator node, so Enter/Space
  // on the thumbnail / Plus buttons keep their native behaviour.
  const { onPointerDown, ...keyboardListeners } = (listeners ?? {}) as Record<
    string,
    ((e: unknown) => void) | undefined
  >
  // The root is not a keyboard target (role presentation, tabIndex -1); the
  // thumbnail, Plus and drag-handle buttons are.
  const handleRootPointerDown =
    renderFailed || !onPointerDown
      ? undefined
      : (e: ReactPointerEvent<HTMLDivElement>) => onPointerDown(e)

  return (
    <div
      ref={mergedRef}
      data-testid="slide-card"
      role="presentation"
      tabIndex={-1}
      title={renderFailed ? t("slideSense.renderFailedTooltip") : undefined}
      className={cn(
        "group/card border-border/45 bg-background/10 relative w-full overflow-hidden rounded-lg border",
        renderFailed
          ? "cursor-not-allowed opacity-60"
          : "card-interactive hover:border-border/70 hover:bg-background/18 cursor-grab",
        isDragging && "ring-primary/30 scale-95 cursor-grabbing opacity-30 shadow-lg ring-2"
      )}
      onPointerDown={handleRootPointerDown}
    >
      {/* Clickable area — this wrapper is a mouse convenience (presentation);
          the thumbnail is the real <button> for keyboard/AT and its click
          bubbles here to open the preview. The Plus button is a sibling of the
          thumbnail button so buttons are never nested. */}
      <div
        className={cn("flex w-full flex-col text-left", !renderFailed && "cursor-pointer")}
        role="presentation"
        onClick={renderFailed ? undefined : () => onPreview?.(slide)}
      >
        {/* Thumbnail image */}
        <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
          <button
            aria-label={slide.ai_title || `${t("slideSense.slides")} ${slide.slide_index + 1}`}
            className="focus-visible:ring-ring absolute inset-0 block h-full w-full cursor-pointer focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
            disabled={renderFailed}
            type="button"
          >
            <SlideThumbnailImage
              alt={slide.ai_title}
              fallbackIcon={<Presentation aria-hidden className="h-6 w-6" />}
              slideId={slide.id}
              thumbnailUrl={slide.thumbnail_url}
            />
          </button>

          {/* Render-failed badge overlay */}
          {renderFailed && (
            <div
              className="bg-destructive/90 text-destructive-foreground absolute inset-x-0 bottom-0 flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium"
              data-testid="slide-render-failed-badge"
            >
              <AlertTriangle className="h-3 w-3 shrink-0" />
              <span className="truncate">{t("slideSense.renderFailed")}</span>
            </div>
          )}

          {/* Hidden-in-source badge — the slide stays searchable/usable in
              the builder, but Presentation Mode skips it (#2336). */}
          {!renderFailed && slide.is_hidden && (
            <div
              className="bg-muted/90 text-muted-foreground absolute top-1.5 left-1.5 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium"
              data-testid="slide-hidden-badge"
              title={t("slideSense.hiddenSlideTooltip")}
            >
              <EyeOff className="h-3 w-3 shrink-0" />
              <span>{t("slideSense.hiddenSlide")}</span>
            </div>
          )}

          {/* Keyboard-reachable drag handle (visible on hover / focus). Carries
              the dnd-kit attributes and the keyboard listener; pointer drag is
              handled by the card root. */}
          {!renderFailed && (
            <button
              ref={setActivatorNodeRef}
              aria-label={`${t("slideSense.dragHandle")}: ${slide.ai_title || `${t("slideSense.slides")} ${slide.slide_index + 1}`}`}
              className="bg-background/90 text-foreground focus-visible:ring-ring absolute top-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-md opacity-0 shadow-sm group-hover/card:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
              data-testid="slide-drag-handle"
              title={t("slideSense.dragHandle")}
              type="button"
              {...attributes}
              {...keyboardListeners}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical aria-hidden className="h-4 w-4" />
            </button>
          )}

          {/* Quick-add button overlay */}
          {onAdd && !renderFailed && (
            <button
              aria-label={`${t("slideSense.addToBuilder")}: ${slide.ai_title || `${t("slideSense.slides")} ${slide.slide_index + 1}`}`}
              className="bg-primary text-primary-foreground border-foreground focus-visible:ring-ring absolute right-1.5 bottom-1.5 flex h-7 w-7 items-center justify-center rounded-full border opacity-0 shadow-md transition-[opacity,scale] duration-150 group-hover/card:opacity-100 hover:scale-110 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
              data-testid="slide-add-button"
              title={t("slideSense.addToBuilder")}
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onAdd(slide)
              }}
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Text content */}
        <div className="space-y-1 p-2.5">
          <p className="text-foreground truncate text-sm leading-snug font-medium">
            {slide.ai_title || `${t("slideSense.slides")} ${slide.slide_index + 1}`}
          </p>

          <div className="flex items-center gap-1.5">
            <SlideCategoryBadge category={slide.category} />
          </div>

          <p className="text-muted-foreground truncate text-[11px]">{slide.source_filename}</p>

          {relativeUpdatedAt && (
            <p className="text-muted-foreground/80 truncate text-[10px]">
              {t("slideSense.lastUpdated", { when: relativeUpdatedAt })}
            </p>
          )}

          {/* AI search reason (when in AI search mode) */}
          {aiSearchReason && (
            <p className="text-primary/70 mt-1 line-clamp-2 text-[11px] italic">{aiSearchReason}</p>
          )}
        </div>
      </div>
    </div>
  )
}
